import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  MenuItem,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  NavigateNext,
  Close,
  DirectionsBoat,
  Event,
  DateRange,
  LocationOn,
} from '@mui/icons-material';
import { Calendar, momentLocalizer, Event as CalendarEventType } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Types
interface CalendarEvent extends CalendarEventType {
  id: string;
  title: string;
  description: string;
  type: 'meeting' | 'task' | 'reminder' | 'event';
  priority: 'low' | 'medium' | 'high';
  boatShowId?: string;
}

interface BoatShow {
  id: string;
  name: string;
  location: string;
  startDate: Date;
  endDate: Date;
  description?: string;
}

// Configuration du calendrier
const localizer = momentLocalizer(moment);

const CalendarPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [events, setEvents] = useState<CalendarEvent[]>([
    {
      id: '1',
      title: 'Réunion équipe',
      description: 'Réunion hebdomadaire avec toute l\'équipe',
      start: new Date(2024, 5, 15, 9, 0),
      end: new Date(2024, 5, 15, 10, 30),
      type: 'meeting',
      priority: 'high'
    },
    {
      id: '2',
      title: 'Démo client',
      description: 'Présentation des nouvelles fonctionnalités',
      start: new Date(2024, 5, 17, 14, 0),
      end: new Date(2024, 5, 17, 15, 0),
      type: 'meeting',
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Date limite projet',
      description: 'Soumission du rapport final',
      start: new Date(2024, 5, 20, 17, 0),
      end: new Date(2024, 5, 20, 17, 0),
      type: 'task',
      priority: 'high'
    }
  ]);

  // Ajout des boat shows
  const [boatShows, setBoatShows] = useState<BoatShow[]>([
    {
      id: '1',
      name: 'Salon Nautique de Paris',
      location: 'Paris, France',
      startDate: new Date(2024, 5, 15),
      endDate: new Date(2024, 5, 20),
      description: 'Le plus grand salon nautique de France'
    },
    {
      id: '2',
      name: 'Monaco Yacht Show',
      location: 'Monaco',
      startDate: new Date(2024, 8, 22),
      endDate: new Date(2024, 8, 25),
      description: 'Salon international des super-yachts'
    },
    {
      id: '3',
      name: 'Cannes Yachting Festival',
      location: 'Cannes, France',
      startDate: new Date(2024, 8, 10),
      endDate: new Date(2024, 8, 15),
      description: 'Premier salon nautique à flot en Europe'
    }
  ]);

  // État pour le boat show sélectionné
  const [selectedBoatShow, setSelectedBoatShow] = useState<string>('');
  const [isBoatShowDialogOpen, setIsBoatShowDialogOpen] = useState(false);
  const [newBoatShow, setNewBoatShow] = useState<Partial<BoatShow>>({
    name: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // +7 jours
  });

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [newEvent, setNewEvent] = useState<Partial<CalendarEvent>>({
    title: '',
    description: '',
    type: 'meeting',
    priority: 'medium',
    start: new Date(),
    end: new Date(Date.now() + 60 * 60 * 1000) // +1 heure
  });

  // Calculer le nombre de rendez-vous par date pour le boat show sélectionné
  const getAppointmentCountByDate = () => {
    const counts: Record<string, number> = {};
    
    if (selectedBoatShow) {
      const selectedShow = boatShows.find(show => show.id === selectedBoatShow);
      
      if (selectedShow) {
        const startDate = new Date(selectedShow.startDate);
        const endDate = new Date(selectedShow.endDate);
        
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          const dateStr = moment(date).format('YYYY-MM-DD');
          counts[dateStr] = events.filter(event => 
            event.boatShowId === selectedBoatShow && 
            moment(event.start).format('YYYY-MM-DD') === dateStr
          ).length;
        }
      }
    }
    
    return counts;
  };

  const appointmentCounts = getAppointmentCountByDate();

  // Ouvrir le modal pour créer un événement
  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    setNewEvent({
      title: '',
      description: '',
      type: 'meeting',
      priority: 'medium',
      start: slotInfo.start,
      end: slotInfo.end,
      boatShowId: selectedBoatShow || undefined
    });
    setSelectedEvent(null);
    setIsDialogOpen(true);
  };

  // Ouvrir le modal pour modifier un événement
  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setNewEvent(event);
    setIsDialogOpen(true);
  };

  // Sauvegarder l'événement
  const handleSaveEvent = () => {
    if (!newEvent.title || !newEvent.start || !newEvent.end) {
      setAlert({ type: 'error', message: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }

    if (selectedEvent) {
      // Modification
      setEvents(prev => prev.map(event => 
        event.id === selectedEvent.id ? { ...newEvent, id: selectedEvent.id } as CalendarEvent : event
      ));
      setAlert({ type: 'success', message: 'Événement modifié avec succès' });
    } else {
      // Création
      const event: CalendarEvent = {
        ...newEvent as CalendarEvent,
        id: Date.now().toString()
      };
      setEvents(prev => [...prev, event]);
      setAlert({ type: 'success', message: 'Événement créé avec succès' });
    }

    setIsDialogOpen(false);
    setSelectedEvent(null);
    setNewEvent({
      title: '',
      description: '',
      type: 'meeting',
      priority: 'medium',
      start: new Date(),
      end: new Date(Date.now() + 60 * 60 * 1000)
    });
  };

  // Sauvegarder un boat show
  const handleSaveBoatShow = () => {
    if (!newBoatShow.name || !newBoatShow.location || !newBoatShow.startDate || !newBoatShow.endDate) {
      setAlert({ type: 'error', message: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }

    const boatShow: BoatShow = {
      ...newBoatShow as BoatShow,
      id: Date.now().toString()
    };
    
    setBoatShows(prev => [...prev, boatShow]);
    setAlert({ type: 'success', message: 'Salon nautique ajouté avec succès' });
    setIsBoatShowDialogOpen(false);
    setNewBoatShow({
      name: '',
      location: '',
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    });
  };

  // Supprimer un événement
  const handleDeleteEvent = () => {
    if (selectedEvent) {
      setEvents(prev => prev.filter(event => event.id !== selectedEvent.id));
      setAlert({ type: 'success', message: 'Événement supprimé avec succès' });
      setIsDeleteDialogOpen(false);
      setSelectedEvent(null);
    }
  };

  // Fermer les modals
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvent(null);
    setNewEvent({
      title: '',
      description: '',
      type: 'meeting',
      priority: 'medium',
      start: new Date(),
      end: new Date(Date.now() + 60 * 60 * 1000)
    });
  };

  const handleCloseBoatShowDialog = () => {
    setIsBoatShowDialogOpen(false);
    setNewBoatShow({
      name: '',
      location: '',
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    });
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedEvent(null);
  };

  // Styles personnalisés pour les événements
  const eventStyleGetter = (event: CalendarEvent) => {
    let backgroundColor = '';
    let borderColor = '';

    switch (event.type) {
      case 'meeting':
        backgroundColor = '#C4B08C';
        borderColor = '#b39d77';
        break;
      case 'task':
        backgroundColor = '#90caf9';
        borderColor = '#64b5f6';
        break;
      case 'reminder':
        backgroundColor = '#a5d6a7';
        borderColor = '#81c784';
        break;
      case 'event':
        backgroundColor = '#ffcc80';
        borderColor = '#ffb74d';
        break;
      default:
        backgroundColor = '#C4B08C';
        borderColor = '#b39d77';
    }

    return {
      style: {
        backgroundColor,
        border: `2px solid ${borderColor}`,
        borderRadius: '8px',
        color: 'white',
        fontWeight: 'bold',
        fontSize: isMobile ? '0.7rem' : '0.8rem',
        padding: '2px 4px'
      }
    };
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, bgcolor: '#FFFFFF' }}>
      {alert && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link color="inherit" href="#" underline="hover">
          HOME
        </Link>
        <Typography color="text.primary">CALENDRIER</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={{ xs: 'column', md: 'row' }} gap={2} mb={4}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: '#333' }}>
          📅 Calendrier des Événements
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DirectionsBoat />}
            onClick={() => setIsBoatShowDialogOpen(true)}
            sx={{
              borderColor: '#C4B08C',
              color: '#000',
              '&:hover': { 
                borderColor: '#b39d77',
                bgcolor: 'rgba(196, 176, 140, 0.08)'
              },
              px: 3,
              py: 1,
              borderRadius: 2
            }}
          >
            Ajouter un Salon
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setSelectedEvent(null);
              setNewEvent({
                title: '',
                description: '',
                type: 'meeting',
                priority: 'medium',
                start: new Date(),
                end: new Date(Date.now() + 60 * 60 * 1000),
                boatShowId: selectedBoatShow || undefined
              });
              setIsDialogOpen(true);
            }}
            sx={{
              bgcolor: '#C4B08C',
              color: '#000',
              '&:hover': { bgcolor: '#b39d77' },
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 4px 14px rgba(196, 176, 140, 0.4)'
            }}
          >
            Nouvel Événement
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 75%' } }}>
          <Paper sx={{ 
            p: { xs: 1, md: 3 }, 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            <Calendar
              localizer={localizer}
              events={events.filter(event => !selectedBoatShow || event.boatShowId === selectedBoatShow)}
              startAccessor="start"
              endAccessor="end"
              style={{ height: isMobile ? 400 : 600 }}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              eventPropGetter={eventStyleGetter}
              messages={{
                next: 'Suivant',
                previous: 'Précédent',
                today: 'Aujourd\'hui',
                month: 'Mois',
                week: 'Semaine',
                day: 'Jour',
                agenda: 'Agenda',
                date: 'Date',
                time: 'Heure',
                event: 'Événement',
                noEventsInRange: 'Aucun événement dans cette période'
              }}
            />
          </Paper>
        </Box>
        
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
          {/* Sélection du Boat Show */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            mb: 3
          }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <DirectionsBoat /> Salons Nautiques
            </Typography>
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="boat-show-select-label">Sélectionner un salon</InputLabel>
              <Select
                labelId="boat-show-select-label"
                value={selectedBoatShow}
                onChange={(e) => setSelectedBoatShow(e.target.value as string)}
                label="Sélectionner un salon"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(196, 176, 140, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(196, 176, 140, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C4B08C',
                  }
                }}
              >
                <MenuItem value="">Tous les événements</MenuItem>
                {boatShows.map((show) => (
                  <MenuItem key={show.id} value={show.id}>
                    {show.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedBoatShow && (
              <Box>
                {boatShows
                  .filter(show => show.id === selectedBoatShow)
                  .map(show => (
                    <Card 
                      key={show.id} 
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        mb: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ 
                        height: 8, 
                        background: 'linear-gradient(90deg, #C4B08C 0%, #d7c7a8 100%)' 
                      }} />
                      <CardContent>
                        <Typography variant="h6" fontWeight={600}>{show.name}</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                          <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {show.location}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mt={0.5}>
                          <DateRange fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {moment(show.startDate).format('DD/MM/YYYY')} - {moment(show.endDate).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                        {show.description && (
                          <Typography variant="body2" mt={1}>
                            {show.description}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            )}
          </Paper>
          
          {/* Rendez-vous par date */}
          {selectedBoatShow && Object.keys(appointmentCounts).length > 0 && (
            <Paper sx={{ 
              p: 3, 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" fontWeight={600} mb={2} sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Event /> Rendez-vous par date
              </Typography>
              <Box>
                {Object.entries(appointmentCounts).map(([date, count]) => (
                  <Box 
                    key={date} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1.5,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(196, 176, 140, 0.08)',
                      border: '1px solid rgba(196, 176, 140, 0.2)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(196, 176, 140, 0.15)',
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {moment(date).format('DD MMMM YYYY')}
                    </Typography>
                    <Chip 
                      label={`${count} rendez-vous`} 
                      size="small"
                      sx={{ 
                        bgcolor: count > 0 ? '#C4B08C' : '#e0e0e0',
                        color: count > 0 ? 'white' : 'text.secondary',
                        fontWeight: 600
                      }} 
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Légende */}
      <Box sx={{ 
        mt: 3, 
        p: 3, 
        bgcolor: '#f8f8f8', 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,.04)'
      }}>
        <Typography variant="h6" gutterBottom fontWeight="600">Légende :</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip label="Réunion" sx={{ bgcolor: '#C4B08C', color: 'white', borderRadius: 2 }} />
          <Chip label="Tâche" sx={{ bgcolor: '#90caf9', color: 'white', borderRadius: 2 }} />
          <Chip label="Rappel" sx={{ bgcolor: '#a5d6a7', color: 'white', borderRadius: 2 }} />
          <Chip label="Événement" sx={{ bgcolor: '#ffcc80', color: 'white', borderRadius: 2 }} />
        </Box>
      </Box>

      {/* Modal d'édition/création */}
      <Dialog 
        open={isDialogOpen} 
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDialog();
          }
        }}
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f8f8f8',
          borderBottom: '1px solid #eaeaea'
        }}>
          <Typography variant="h6" fontWeight="600">
            {selectedEvent ? 'Modifier l\'événement' : 'Créer un événement'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Titre *"
              value={newEvent.title || ''}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newEvent.description || ''}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2 }}>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={newEvent.type || 'meeting'}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value as any })}
                >
                  <MenuItem value="meeting">Réunion</MenuItem>
                  <MenuItem value="task">Tâche</MenuItem>
                  <MenuItem value="reminder">Rappel</MenuItem>
                  <MenuItem value="event">Événement</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TextField
                  fullWidth
                  select
                  label="Priorité"
                  value={newEvent.priority || 'medium'}
                  onChange={(e) => setNewEvent({ ...newEvent, priority: e.target.value as any })}
                >
                  <MenuItem value="low">Basse</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Haute</MenuItem>
                </TextField>
              </Box>
            </Box>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2 }}>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Début *"
                  InputLabelProps={{ shrink: true }}
                  value={moment(newEvent.start).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => setNewEvent({ ...newEvent, start: new Date(e.target.value) })}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Fin *"
                  InputLabelProps={{ shrink: true }}
                  value={moment(newEvent.end).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => setNewEvent({ ...newEvent, end: new Date(e.target.value) })}
                />
              </Box>
            </Box>
            <FormControl fullWidth>
              <InputLabel id="boat-show-event-label">Salon Nautique</InputLabel>
              <Select
                labelId="boat-show-event-label"
                value={newEvent.boatShowId || ''}
                onChange={(e) => setNewEvent({ ...newEvent, boatShowId: e.target.value as string })}
                label="Salon Nautique"
              >
                <MenuItem value="">Aucun salon</MenuItem>
                {boatShows.map((show) => (
                  <MenuItem key={show.id} value={show.id}>
                    {show.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eaeaea' }}>
          {selectedEvent && (
            <Button 
              color="error" 
              onClick={() => {
                setIsDialogOpen(false);
                setIsDeleteDialogOpen(true);
              }}
              startIcon={<Delete />}
            >
              Supprimer
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEvent}
            sx={{ 
              bgcolor: '#C4B08C', 
              color: '#000',
              '&:hover': { bgcolor: '#b39d77' },
              px: 3
            }}
          >
            {selectedEvent ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'ajout de boat show */}
      <Dialog 
        open={isBoatShowDialogOpen} 
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseBoatShowDialog();
          }
        }} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f8f8f8',
          borderBottom: '1px solid #eaeaea'
        }}>
          <Typography variant="h6" fontWeight="600">
            Ajouter un Salon Nautique
          </Typography>
          <IconButton onClick={handleCloseBoatShowDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Nom du salon *"
            value={newBoatShow.name || ''}
            onChange={(e) => setNewBoatShow({ ...newBoatShow, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Lieu *"
            value={newBoatShow.location || ''}
            onChange={(e) => setNewBoatShow({ ...newBoatShow, location: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <TextField
                fullWidth
                type="date"
                label="Date de début *"
                InputLabelProps={{ shrink: true }}
                value={moment(newBoatShow.startDate).format('YYYY-MM-DD')}
                onChange={(e) => setNewBoatShow({ ...newBoatShow, startDate: new Date(e.target.value) })}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <TextField
                fullWidth
                type="date"
                label="Date de fin *"
                InputLabelProps={{ shrink: true }}
                value={moment(newBoatShow.endDate).format('YYYY-MM-DD')}
                onChange={(e) => setNewBoatShow({ ...newBoatShow, endDate: new Date(e.target.value) })}
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={newBoatShow.description || ''}
            onChange={(e) => setNewBoatShow({ ...newBoatShow, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eaeaea' }}>
          <Button onClick={handleCloseBoatShowDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveBoatShow}
            sx={{ 
              bgcolor: '#C4B08C', 
              color: '#000',
              '&:hover': { bgcolor: '#b39d77' },
              px: 3
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmation de suppression */}
      <Dialog 
        open={isDeleteDialogOpen} 
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDeleteDialog();
          }
        }} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'événement "{selectedEvent?.title}" ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={handleDeleteEvent}
            startIcon={<Delete />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CalendarPage;